import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money, relative, dateFmt } from "@/lib/format";
import { pushRecent } from "@/components/CommandPalette";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormDialog } from "@/components/common/FormDialog";
import { FileText, Repeat, Pin, Star, Plus, Trash2, Pencil } from "lucide-react";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { QuotationsTab } from "@/pages/crm/tabs/QuotationsTab";
import { TasksTab } from "@/pages/crm/tabs/TasksTab";
import { RequirementsTab } from "@/pages/crm/tabs/RequirementsTab";
import { PaymentTermsTab } from "@/pages/crm/tabs/PaymentTermsTab";
import { toast } from "sonner";

const ACT_TABS = ["all", "note", "call", "meeting", "email"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const [addOpen, setAddOpen] = useState(null);
  const [editAct, setEditAct] = useState(null); // activity being edited

  const { data: lead, isLoading } = useQuery({ queryKey: ["lead", id], queryFn: () => get(`/crm/leads/${id}`) });
  const { data: pipelines = [] } = useQuery({ queryKey: ["pipelines"], queryFn: () => get("/crm/pipelines") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const pipeline = pipelines.find((p) => p.id === lead?.pipeline_id);

  useEffect(() => { if (lead) pushRecent({ title: lead.company_name, path: `/crm/leads/${id}` }); }, [lead, id]);

  const addAct = useMutation({
    mutationFn: (body) => post(`/crm/leads/${id}/activities`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Activity added"); setAddOpen(null); },
  });
  const editActMut = useMutation({
    mutationFn: ({ aid, body }) => patch(`/crm/leads/${id}/activities/${aid}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Activity updated"); setEditAct(null); },
  });
  const deleteAct = useMutation({
    mutationFn: (aid) => del(`/crm/leads/${id}/activities/${aid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Activity deleted"); },
  });
  const togglePin = useMutation({
    mutationFn: ({ aid, pinned }) => patch(`/crm/leads/${id}/activities/${aid}`, { pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", id] }),
  });
  const addContact = useMutation({
    mutationFn: (body) => post(`/crm/leads/${id}/contacts`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Contact added"); },
  });
  const editContact = useMutation({
    mutationFn: ({ cid, body }) => patch(`/crm/leads/${id}/contacts/${cid}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Contact updated"); },
  });
  const deleteContact = useMutation({
    mutationFn: (cid) => del(`/crm/leads/${id}/contacts/${cid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", id] }); toast.success("Contact deleted"); },
    onError: () => toast.error("A lead must have at least one contact"),
  });
  const [editContactId, setEditContactId] = useState(null);

  if (isLoading || !lead) return <Skeleton className="h-96" />;
  const stage = pipeline?.stages.find((s) => s.id === lead.stage_id);
  const isWon = stage?.type === "won";

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate("/crm/leads")} className="hover:underline">Leads</button> / {lead.company_name}
      </div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{lead.company_name}</h1>
          {lead.existing_client_id && <StatusBadge family="violet" label="Repeat Client" />}
          <StatusBadge status={stage?.name} />
        </div>
        <div className="flex items-center gap-2">
          <span className="mr-2 font-display text-xl font-bold text-foreground">{money(lead.deal_value, lead.currency)}</span>
          {can("crm", "create") && (
            isWon
              ? <Button data-testid="convert-client" className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={async () => { const c = await post(`/crm/leads/${id}/convert`, {}); toast.success("Converted to client"); navigate(`/crm/clients/${c.id}`); }}>Convert to Client</Button>
              : <Button variant="outline" data-testid="create-quote" onClick={() => navigate(`/crm/leads/${id}/quotation/new`)}><FileText className="mr-1.5 h-4 w-4" />Create Quotation</Button>
          )}
          {can("crm", "delete") && (
            <ConfirmDelete testid="del-lead" title="Delete this lead?"
              trigger={<Button variant="outline" size="icon" data-testid="del-lead-btn"><Trash2 className="h-4 w-4" /></Button>}
              onConfirm={async () => { await del(`/crm/leads/${id}`); toast.success("Lead deleted"); navigate("/crm/leads"); }} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
        <div>
          <Tabs defaultValue="all">
            <TabsList data-testid="lead-tabs">
              {ACT_TABS.map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`lead-tab-${t}`}>{t === "all" ? "All" : t + "s"}</TabsTrigger>)}
              <TabsTrigger value="requirements" data-testid="lead-tab-requirements">Requirements</TabsTrigger>
              <TabsTrigger value="quotations" data-testid="lead-tab-quotations">Quotations</TabsTrigger>
              <TabsTrigger value="payment-terms" data-testid="lead-tab-payment-terms">Payment Terms</TabsTrigger>
              <TabsTrigger value="description" data-testid="lead-tab-description">Description</TabsTrigger>
            </TabsList>

            {ACT_TABS.map((t) => {
              const acts = (lead.activities || []).filter((a) => t === "all" || a.type === t);
              const sorted = [...acts].sort((a, b) => (b.pinned - a.pinned) || (new Date(b.created_at) - new Date(a.created_at)));
              return (
                <TabsContent key={t} value={t} className="mt-4">
                  {t !== "all" && can("crm", "create") && (
                    <Button size="sm" variant="outline" className="mb-3" data-testid={`add-${t}`} onClick={() => setAddOpen(t)}>
                      <Plus className="mr-1 h-3.5 w-3.5" />Add {t}
                    </Button>
                  )}
                  <div className="space-y-2">
                    {sorted.length === 0 ? <EmptyState icon={FileText} title={`No ${t === "all" ? "activity" : t + "s"} yet`} /> :
                      sorted.map((a) => (
                        <div key={a.id} data-testid={`activity-${a.id}`} className="group rounded-lg border border-border bg-card p-3">
                          <div className="flex items-center gap-2">
                            <Avatar user={umap[a.author_id]} size="sm" />
                            <span className="text-[13px] font-medium">{umap[a.author_id]?.name}</span>
                            <StatusBadge family="slate" label={a.type} />
                            {a.pinned && <Pin className="h-3 w-3 fill-amber-500 text-amber-500" />}
                            <span className="ml-auto text-[11px] text-muted-foreground">{relative(a.created_at)}</span>
                            {can("crm", "edit") && (
                              <div className="hidden items-center gap-0.5 group-hover:flex">
                                <button data-testid={`pin-activity-${a.id}`} title={a.pinned ? "Unpin" : "Pin"}
                                  onClick={() => togglePin.mutate({ aid: a.id, pinned: !a.pinned })}
                                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                                  <Pin className="h-3.5 w-3.5" />
                                </button>
                                <button data-testid={`edit-activity-${a.id}`} title="Edit"
                                  onClick={() => setEditAct(a)}
                                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                {can("crm", "delete") && (
                                  <ConfirmDelete testid={`del-activity-${a.id}`} title="Delete this entry?"
                                    trigger={<button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                                    onConfirm={() => deleteAct.mutate(a.id)} />
                                )}
                              </div>
                            )}
                          </div>
                          <p className="mt-2 text-[13px] text-foreground">{a.content}</p>
                        </div>
                      ))}
                  </div>
                </TabsContent>
              );
            })}
            <TabsContent value="requirements" className="mt-4">
              <RequirementsTab lead={lead} />
            </TabsContent>
            <TabsContent value="quotations" className="mt-4">
              <QuotationsTab lead={lead} users={users} />
            </TabsContent>
            <TabsContent value="payment-terms" className="mt-4">
              <PaymentTermsTab lead={lead} />
            </TabsContent>
            <TabsContent value="description" className="mt-4">
              <div className="rounded-lg border border-border bg-card p-4 text-sm text-foreground">{lead.description || "No description."}</div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-display text-sm font-semibold">Lead Info</h3>
            <dl className="mt-3 space-y-2.5 text-sm">
              <Row label="Source"><span className="capitalize">{lead.source}</span></Row>
              <Row label="Owner">
                {can("crm", "edit") ? (
                  <Select value={lead.owner_id} onValueChange={(v) => { patch(`/crm/leads/${id}`, { owner_id: v }).then(() => qc.invalidateQueries({ queryKey: ["lead", id] })); }}>
                    <SelectTrigger className="h-7 w-40" data-testid="owner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : umap[lead.owner_id]?.name}
              </Row>
              <Row label="Probability">{lead.probability}%</Row>
              <Row label="Expected Close">{dateFmt(lead.expected_close_date)}</Row>
              <Row label="Created">{dateFmt(lead.created_at)}</Row>
            </dl>
            <div className="mt-3 flex flex-wrap gap-1">
              {lead.tags?.map((t) => <span key={t} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t}</span>)}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-sm font-semibold">Contacts</h3>
              <FormDialog title="Add Contact" testid="add-contact-modal"
                trigger={<button data-testid="add-contact-btn" className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus className="h-3.5 w-3.5" />Add</button>}
                fields={[
                  { name: "name", label: "Name", required: true, full: true },
                  { name: "email", label: "Email", type: "email" },
                  { name: "phone", label: "Phone" },
                  { name: "role", label: "Role", type: "select", full: true, options: ["Decision Maker", "Technical POC", "Budget Holder", "Other"] },
                ]}
                onSubmit={(v) => addContact.mutateAsync(v)} />
            </div>
            <div className="mt-3 space-y-2">
              {lead.contacts?.map((c) => (
                <div key={c.id} className="group flex items-center gap-2">
                  <Avatar name={c.name} size="sm" color="#64748B" />
                  <div className="min-w-0 flex-1"><div className="text-[13px] font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.role}</div></div>
                  {c.primary && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                  {can("crm", "edit") && (
                    <div className="hidden items-center gap-0.5 group-hover:flex">
                      <button data-testid={`edit-contact-${c.id}`} title="Edit" onClick={() => setEditContactId(c.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {can("crm", "delete") && (
                        <ConfirmDelete testid={`del-contact-${c.id}`} title={`Remove ${c.name}?`}
                          description="Blocked if this is the lead's only contact."
                          trigger={<button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                          onConfirm={() => deleteContact.mutate(c.id)} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4" data-testid="sidebar-tasks-card">
            <h3 className="mb-3 font-display text-sm font-semibold">Tasks</h3>
            <TasksTab lead={lead} users={users} />
          </div>
        </div>
      </div>

      <AddActivityModal type={addOpen} setType={setAddOpen} onSubmit={(body) => addAct.mutate(body)} authorId={me?.id} />
      <EditActivityModal activity={editAct} setActivity={setEditAct}
        onSubmit={(content) => editActMut.mutate({ aid: editAct.id, body: { content } })} />
      {editContactId && (
        <EditContactModal contact={lead.contacts.find((c) => c.id === editContactId)}
          onClose={() => setEditContactId(null)}
          onSubmit={(body) => editContact.mutate({ cid: editContactId, body }, { onSuccess: () => setEditContactId(null) })} />
      )}
    </div>
  );
}

function Row({ label, children }) {
  return <div className="flex items-center justify-between gap-2"><dt className="text-muted-foreground">{label}</dt><dd className="font-medium text-foreground">{children}</dd></div>;
}

function EditActivityModal({ activity, setActivity, onSubmit }) {
  const [content, setContent] = useState("");
  useEffect(() => { if (activity) setContent(activity.content); }, [activity]);
  return (
    <Dialog open={!!activity} onOpenChange={(v) => !v && setActivity(null)}>
      <DialogContent className="max-w-md" data-testid="edit-activity-modal">
        <DialogHeader><DialogTitle className="capitalize">Edit {activity?.type}</DialogTitle></DialogHeader>
        <Label>Details</Label>
        <Textarea data-testid="edit-activity-content" value={content} onChange={(e) => setContent(e.target.value)} rows={4} className="mt-1" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setActivity(null)}>Cancel</Button>
          <Button data-testid="save-edit-activity" onClick={() => onSubmit(content)} disabled={!content}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditContactModal({ contact, onClose, onSubmit }) {
  const [form, setForm] = useState(contact);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!contact} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-contact-modal">
        <DialogHeader><DialogTitle>Edit Contact</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input className="mt-1" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Email</Label><Input className="mt-1" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>Phone</Label><Input className="mt-1" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="col-span-2"><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["Decision Maker", "Technical POC", "Budget Holder", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-edit-contact" onClick={() => onSubmit({ name: form.name, email: form.email, phone: form.phone, role: form.role })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddActivityModal({ type, setType, onSubmit, authorId }) {
  const [content, setContent] = useState("");
  const submit = () => { onSubmit({ type, content, author_id: authorId, pinned: false }); setContent(""); };
  return (
    <Dialog open={!!type} onOpenChange={(v) => !v && setType(null)}>
      <DialogContent className="max-w-md" data-testid="add-activity-modal">
        <DialogHeader><DialogTitle className="capitalize">Add {type}</DialogTitle></DialogHeader>
        <Label>Details</Label>
        <Textarea data-testid="activity-content" value={content} onChange={(e) => setContent(e.target.value)} rows={4} className="mt-1" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setType(null)}>Cancel</Button>
          <Button data-testid="save-activity" onClick={submit} disabled={!content}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
